import FS from "@isomorphic-git/lightning-fs";
import {
  REPOSITORY_EMAIL,
  REPOSITORY_VEYO,
  REPOSITORY_VOICE,
  TRACK_EVENTS,
} from "core/consts";
import { concatStrings } from "core/model/utils/strings";
import { differenceInSeconds } from "date-fns";
import * as git from "isomorphic-git";
import http from "isomorphic-git/http/web";
import jsonAbc from "jsonabc";
import { useCallback, useEffect, useRef } from "react";
import { useTracking } from "react-tracking";
import { locales } from "../index";
import { getLocale } from "../scripts/utils/jsonTools";
import {
  EmailTranslation,
  LocalesType,
  RepositoryType,
  TranslationType,
  TranslationsAndRepo,
} from "../types";
import { convertDbToIdJson, convertDbToLocaleJson } from "../utils";

const authObj = {
  token: "7b205df264e3d29bee9e707286dc434c69a2a925",
  username: "recarebot",
};

const author = {
  email: "recarebot@recaresolutions.com",
  name: "recarebot",
};
/** Exclude prettier and prettierPlugins from compilation
 *  this prevent critical warning "Critical dependency: the request of a dependency is an expression"
 *  Also, it shortens compilation time ( storybook:build benefits a lot from it ), since these 2 packages are huge.
 *  In order to achieve so, prettier and prettierPlugin are globally declared and included directly from the template
 *  https://prettier.io/docs/en/browser.html#global
 */
type PrettierOptions = {
  parser: string;
  plugins: any;
  trailingComma: string;
};
declare const prettier: {
  format: (source: string, options?: PrettierOptions) => string;
};
declare const prettierPlugins: PrettierOptions["plugins"];

async function writeOnExistingFile(
  filepath: string,
  content: string,
  {
    dir,
    fs,
    gitData,
  }: { dir: string; fs: git.PromiseFsClient; gitData: AnyObject },
  makeItPretty?: boolean,
) {
  const shouldApplyPrettier = makeItPretty != null ? makeItPretty : true;
  const prettyContent = shouldApplyPrettier
    ? prettier.format(content, {
        parser: "typescript",
        plugins: prettierPlugins,
        trailingComma: "all",
      })
    : content;
  await fs.promises.writeFile(
    concatStrings(dir, filepath, "/"),
    prettyContent,
    "utf8",
  );
  await git.add({
    ...gitData,
    dir,
    filepath,
    fs,
  });
}

const handleEmailTranslationFile = async ({
  dir,
  emailTranslations,
  fs,
  gitData,
}: {
  dir: string;
  emailTranslations: Readonly<EmailTranslation[]>;
  fs: git.PromiseFsClient;
  gitData: AnyObject;
}) => {
  const localePath = `src/translations/index.ts`;
  const parsedTranslationsObject: AnyObject = {};

  emailTranslations?.forEach((translation) => {
    const hasNestedKeys = translation.id.includes(".");

    if (!hasNestedKeys) {
      parsedTranslationsObject[translation.id] = translation.translation;
    } else {
      const paths = translation.id.split(".");

      paths.reduce((acc, key, index) => {
        acc[key] =
          index === paths.length - 1
            ? translation.translation
            : acc[key]
            ? acc[key]
            : {};

        return acc[key];
      }, parsedTranslationsObject);
    }
  });

  const printableLocale = `export const translations = ${jsonAbc.sort(
    JSON.stringify(parsedTranslationsObject, null, 2),
    true,
  )} as const;`;

  await writeOnExistingFile(localePath, printableLocale, {
    dir,
    fs,
    gitData,
  });
};

const handleTranslationFiles = async ({
  dir,
  fs,
  gitData,
  repo,
  setStatus,
  translations,
}: {
  dir: string;
  fs: git.PromiseFsClient;
  gitData: AnyObject;
  repo: RepositoryType;
  setStatus: (status: string) => void;
  translations: Readonly<TranslationType[]>;
}) => {
  const translationsPath =
    repo === REPOSITORY_VOICE
      ? "src/translations"
      : "projects/app/src/translations";
  await Promise.all(
    Object.keys(locales).map(async (locale) => {
      setStatus(`Writing ${locales[locale]} translation file`);
      const localePath = `${translationsPath}/locales/${locale}.ts`;
      const printableLocale = getLocale(
        locale,
        JSON.stringify(
          convertDbToLocaleJson(locale as keyof LocalesType, translations),
        ),
        JSON.stringify(convertDbToIdJson(translations)),
        translations,
      );
      if (!fs) return Promise.reject("no fs");
      return writeOnExistingFile(localePath, printableLocale, {
        dir,
        fs,
        gitData,
      });
    }),
  );
};

const useGitHandler = () => {
  const fs = useRef<git.PromiseFsClient | null>(null);
  const { trackEvent } = useTracking();

  useEffect(() => {
    if (!fs.current) {
      const fsInstance = new FS("fs", {
        wipe: true,
      });
      fs.current = fsInstance;
    }
    return () => {
      // Cleanup on unmount
      if (fs.current) {
        fs.current = null;
      }
    };
  }, []);

  const commitFiles = useCallback(
    async ({
      emailTranslations,
      repo,
      setStatus,
      translations,
    }: {
      setStatus: (status: string) => void;
    } & TranslationsAndRepo) => {
      const dir = "/gitplayground";
      const url = `https://github.com/recare/${repo}`;
      const isEmailRepo = repo === REPOSITORY_EMAIL;

      const hasTranslations =
        (!isEmailRepo && translations) || (isEmailRepo && emailTranslations);

      if (!hasTranslations || !fs.current) {
        return;
      }

      const start = new Date();
      const refName = "refs/heads/translations-import";
      const gitData = {
        corsProxy: "https://corsbuster.recaresolutions.com",
        dir,
        fs: fs.current,
        headers: {
          Authorization: `Basic ${Buffer.from(
            `${authObj.username}:${authObj.token}`,
          ).toString("base64")}`,
        },
        http,
        url,
      };

      try {
        setStatus("Creating virtual file system");
        await fs.current.promises.mkdir(dir);

        setStatus("Cloning repository into file system");
        await git.clone({
          ...gitData,
          depth: 1,
          ref: repo === REPOSITORY_VEYO ? "master" : "main",
          singleBranch: true,
        });

        setStatus("Checking out to correct branch");
        try {
          await git.fetch({
            ...gitData,
            depth: 1,
            ref: refName,
            singleBranch: true,
            tags: false,
          });
          await git.checkout({ dir, fs: fs.current, ref: refName });
        } catch (_) {
          await git.branch({
            checkout: true,
            dir,
            fs: fs.current,
            ref: refName,
          });
        }

        if (isEmailRepo) {
          await handleEmailTranslationFile({
            dir,
            emailTranslations,
            fs: fs.current,
            gitData,
          });
        } else {
          await handleTranslationFiles({
            dir,
            fs: fs.current,
            gitData,
            repo,
            setStatus,
            translations,
          });
        }

        setStatus("Committing changes to repository");
        await git.commit({
          ...gitData,
          author,
          fs: fs.current,
          message: "ACP Import",
          ref: refName,
        });

        setStatus("Pushing changes to repository");
        try {
          await git.push({
            ...gitData,
            force: true,
            fs: fs.current,
            http,
            ref: refName,
          });
        } catch (_error) {
          // If push fails, try to fetch and push again
          await git.fetch({
            ...gitData,
            depth: 1,
            ref: refName,
            singleBranch: true,
            tags: false,
          });
          await git.push({
            ...gitData,
            force: true,
            fs: fs.current,
            http,
            ref: refName,
          });
        }

        trackEvent({
          duration_in_seconds: differenceInSeconds(new Date(), start),
          name: TRACK_EVENTS.ACP_TRANSLATIONS_EXPORT,
        });
      } catch (error) {
        console.error("Git operation failed:", error);
        throw error;
      } finally {
        if (!isEmailRepo) {
          fs.current = null;
        }
      }
    },
    [],
  );

  return commitFiles;
};

export default useGitHandler;
